// Question-and-answer blocks for BlueBear articles.
//
// Two outputs from one source of truth so they can never drift:
//   1. Visible `<dl>` prose appended to the article body. This is the part that actually earns
//      anything — a crawler, an answer engine, and a human all read the same words.
//   2. A `FAQPage` schema node.
//
// Write the question in the phrasing a practitioner would type ("why is my OpenAI API bill so
// high"), not in a polished marketing register. Answer it in the first sentence, then qualify.

export const ARTICLE_FAQ_VERSION = 'bluebear.article-faq.v1';

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function plainText(value = '') {
  return String(value)
    .replace(/<[^>]+>/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Answers may carry inline links, so they are authored as HTML fragments and passed through.
 * Questions are plain text and are escaped.
 */
export function buildFaqHtml(faqs = [], { heading = 'Questions people actually search for' } = {}) {
  if (!faqs.length) return '';
  const items = faqs
    .map((faq) => `  <dt><strong>${escapeHtml(faq.question)}</strong></dt>\n  <dd>${faq.answer}</dd>`)
    .join('\n');
  return `<h2>${escapeHtml(heading)}</h2>\n<dl class="article-faq">\n${items}\n</dl>`;
}

export function buildFaqSchema(faqs = [], pageUrl) {
  if (!faqs.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    ...(pageUrl ? { '@id': `${pageUrl}#faq`, url: pageUrl, mainEntityOfPage: { '@id': pageUrl } } : {}),
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: plainText(faq.question),
      acceptedAnswer: { '@type': 'Answer', text: plainText(faq.answer) },
    })),
  };
}

/**
 * Appends the visible block once. Idempotent so re-running the editorial policy over an
 * already-processed corpus cannot duplicate it.
 */
export function appendFaqSection(contentHtml, faqs, options) {
  const html = buildFaqHtml(faqs, options);
  if (!html) return contentHtml;
  const headingMarker = html.slice(0, html.indexOf('</h2>') + 5);
  if (contentHtml.includes(headingMarker)) return contentHtml;
  return `${contentHtml}\n\n${html}`;
}
